/**
 * Boss à trois phases. Port de ABossDrone, avec la correction du corridor (gamedesign §10) :
 * - « hold » : le boss freine et s'immobilise pendant l'annonce, le corridor part de lui ;
 * - « attached » : le corridor suit la position du boss, direction figée au début de l'annonce.
 */
import type { BossPhase } from '../../../data';
import { Quat, Vec3, UP } from '../../core/math';
import { Ship, type BossStatus, type ShipCommand } from '../../entities';
import { aiMotorProfile } from '../../flight/motor';
import type { SimState } from '../../state';
import { spawnEnemyBolt } from '../../weapons/projectiles';
import { EnemyBrain } from './enemy';

export class BossBrain extends EnemyBrain {
  phase: BossPhase = 'opening';
  private recovery = 0;
  private volleyCharging = false;
  private timeUntilVolley = 0;
  private volleyAim = new Vec3();
  private specialTelegraph = false;
  private specialRemaining = 0;
  private timeUntilSpecial = 0;
  private specialOrigin = new Vec3();
  private specialDirection = new Vec3(0, 0, -1);
  private specialAnchor = new Vec3();
  private summonTimer = 0;

  constructor(state: SimState, ship: Ship) {
    super(state, ship, 'boss', null);
    this.enterPhase('opening', false);
  }

  protected override get fireRange(): number { return this.state.data.boss.fireRange; }
  protected override get projectileSpeed(): number { return this.state.data.boss.projectileSpeed; }
  protected override get requiredFacing(): number { return this.state.data.boss.requiredFacingDot; }
  protected override get weaponForward(): number { return this.state.data.boss.weaponForward; }
  protected override get boltColor(): [number, number, number] { return [0.85, 0.02, 1]; }

  status(): BossStatus {
    return {
      phase: this.phase,
      cue: this.cue(),
      volleyCharging: this.volleyCharging,
      specialTelegraph: this.specialTelegraph,
      recovery: this.recovery,
    };
  }

  cue(): string {
    if (this.specialTelegraph) return 'CHARGE : QUITTEZ LES LIGNES VIOLETTES';
    if (this.volleyCharging) return 'SALVE : PRÉPAREZ UNE ESQUIVE';
    if (this.recovery > 0) return 'RÉCUPÉRATION : ATTAQUEZ';
    return this.phase === 'opening' ? 'PASSES D’ATTAQUE' : 'GARDEZ VOTRE MOBILITÉ';
  }

  private enterPhase(phase: BossPhase, announce = true): void {
    const boss = this.state.data.boss;
    const tuning = boss.phases[phase];
    this.phase = phase;
    this.usesAttackPasses = phase === 'opening';
    this.timeUntilVolley = boss.volley.firstDelay;
    if (phase === 'overdrive') this.timeUntilSpecial = boss.special.firstDelay;
    if (phase === 'assault') this.summonTimer = boss.summon.firstDelay;
    this.recovery = boss.phaseEntryRecovery;
    this.tuning = {
      maxSpeed: tuning.maxSpeed,
      fireInterval: tuning.fireInterval,
      damage: tuning.damage,
      preferredDistance: tuning.preferredDistance,
      retreatDistance: Math.min(tuning.retreatDistance, tuning.preferredDistance),
    };
    this.ship.motor = bossMotor(this.state, phase);
    if (announce) this.state.emit({ type: 'bossPhase', time: this.state.time, phase });
  }

  private updatePhase(): void {
    const boss = this.state.data.boss;
    const ratio = this.ship.healthRatio();
    if (ratio <= boss.overdriveThreshold && this.phase !== 'overdrive') this.enterPhase('overdrive');
    else if (ratio <= boss.assaultThreshold && this.phase === 'opening') this.enterPhase('assault');
  }

  override update(dt: number): ShipCommand {
    const state = this.state;
    const boss = state.data.boss;
    if (!this.ship.alive) return super.update(dt);
    this.updatePhase();
    this.recovery = Math.max(0, this.recovery - dt);
    if (this.specialTelegraph) {
      this.specialRemaining -= dt;
      if (this.specialRemaining <= 0) this.fireSpecial();
    }
    if (this.phase !== 'opening') {
      this.summonTimer -= dt;
      if (this.summonTimer <= 0) {
        this.summonDrones();
        this.summonTimer = boss.summon.interval;
      }
    }
    this.combatSuppressed = this.specialTelegraph || this.volleyCharging || this.recovery > 0;
    this.holdPosition = this.specialTelegraph && boss.special.corridorMode === 'hold';
    const command = super.update(dt);

    const player = state.player;
    if (!player.alive || this.specialTelegraph || this.recovery > 0) return command;
    if (this.volleyCharging) {
      this.timeUntilVolley -= dt;
      if (this.timeUntilVolley <= 0) {
        this.fireVolley();
        this.volleyCharging = false;
        this.recovery = Math.max(0, boss.volley.recoveryDuration);
        this.timeUntilVolley = boss.volley.interval;
      }
      return command;
    }
    if (this.phase === 'overdrive') {
      this.timeUntilSpecial -= dt;
      if (this.timeUntilSpecial <= 0) {
        this.beginSpecial();
        this.timeUntilSpecial = boss.special.interval;
        return command;
      }
    }
    if (this.phase !== 'opening') {
      this.timeUntilVolley -= dt;
      if (this.timeUntilVolley <= 0) {
        this.volleyCharging = true;
        this.timeUntilVolley = Math.max(0.05, boss.volley.chargeDuration);
        this.volleyAim = player.position.clone();
        state.emit({
          type: 'telegraph',
          time: state.time,
          shipId: this.ship.id,
          kind: 'volley',
          lanes: this.volleyOrigins().map((o) => ({ origin: o.toArray(), end: this.volleyAim.toArray() })),
          duration: this.timeUntilVolley,
          color: [1, 0.5, 0.05],
          anchor: this.ship.position.toArray(),
          follow: true,
        });
      }
    }
    return command;
  }

  /** Escorte de drones autour du boss (pression arcade pendant le combat final). */
  private summonDrones(): void {
    const s = this.state.data.boss.summon;
    const rng = this.state.rng;
    const toPlayer = this.state.player.position.sub(this.ship.position).normalized(this.ship.forward());
    for (let i = 0; i < s.count; i++) {
      const offset = new Vec3(rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1)).normalized(UP()).scale(s.radius);
      this.state.spawnQueue.push({ role: 'drone', position: this.ship.position.add(offset), rotation: Quat.lookRotation(toPlayer, UP()) });
    }
  }

  private volleyOrigins(): Vec3[] {
    const v = this.state.data.boss.volley;
    const ship = this.ship;
    const base = ship.position.add(ship.forward().scale(this.weaponForward));
    return [-v.sideOffset, v.sideOffset].map((side) => base.add(ship.right().scale(side)));
  }

  private fireVolley(): void {
    const v = this.state.data.boss.volley;
    for (const origin of this.volleyOrigins()) {
      spawnEnemyBolt(this.state, {
        kind: 'volley',
        owner: this.ship,
        source: 'boss',
        origin,
        direction: this.volleyAim.sub(origin),
        speed: v.projectileSpeed,
        range: v.range,
        damage: this.phase === 'overdrive' ? v.overdriveDamage : v.damage,
        color: [1, 0.22, 0.01],
      });
    }
  }

  private laneOffsets(): Vec3[] {
    const s = this.state.data.boss.special;
    const right = Quat.lookRotation(this.specialDirection, UP()).right();
    const half = (s.laneCount - 1) / 2;
    return Array.from({ length: s.laneCount }, (_, i) => right.scale((i - half) * s.laneSpacing));
  }

  private beginSpecial(): void {
    const state = this.state;
    const s = state.data.boss.special;
    const player = state.player;
    this.specialTelegraph = true;
    this.specialRemaining = Math.max(0.05, s.chargeDuration);
    this.specialOrigin = this.ship.position.add(this.ship.forward().scale(this.weaponForward));
    this.specialDirection = player.position.sub(this.specialOrigin).normalized(this.ship.forward());
    this.specialAnchor = this.ship.position.clone();
    state.emit({
      type: 'telegraph',
      time: state.time,
      shipId: this.ship.id,
      kind: 'special',
      lanes: this.laneOffsets().map((offset) => {
        const origin = this.specialOrigin.add(offset);
        return { origin: origin.toArray(), end: origin.add(this.specialDirection.scale(s.range)).toArray() };
      }),
      duration: this.specialRemaining,
      color: [0.72, 0.02, 1],
      anchor: this.specialAnchor.toArray(),
      follow: s.corridorMode === 'attached',
    });
  }

  private fireSpecial(): void {
    const state = this.state;
    const s = state.data.boss.special;
    this.specialTelegraph = false;
    this.recovery = Math.max(0, s.recoveryDuration);
    if (!state.player.alive) return;
    // Direction figée au début de l'annonce ; origine selon le mode de corridor.
    const origin = s.corridorMode === 'attached'
      ? this.specialOrigin.add(this.ship.position.sub(this.specialAnchor))
      : this.specialOrigin;
    for (const offset of this.laneOffsets()) {
      spawnEnemyBolt(state, {
        kind: 'special',
        owner: this.ship,
        source: 'boss',
        origin: origin.add(offset),
        direction: this.specialDirection,
        speed: s.projectileSpeed,
        range: s.range,
        damage: s.damage,
        color: [0.72, 0.02, 1],
      });
    }
  }
}

function bossMotor(state: SimState, phase: BossPhase) {
  const boss = state.data.boss;
  const common = state.data.enemies.common;
  return aiMotorProfile({
    maxSpeed: boss.phases[phase].maxSpeed,
    acceleration: boss.acceleration,
    turnRateDeg: boss.turnRateDeg,
    releaseDeceleration: common.releaseDeceleration,
    brakeDeceleration: common.brakeDeceleration,
    turningBoost: common.turningBoost,
  });
}

export function createBoss(state: SimState, position: Vec3, rotation: Quat): { ship: Ship; brain: BossBrain } {
  const boss = state.data.boss;
  const ship = new Ship(state.newId(), 'boss', 'enemy', position, rotation, boss.health, boss.collisionRadius, bossMotor(state, 'opening'));
  return { ship, brain: new BossBrain(state, ship) };
}
